import { useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import { getClaimType, UNVERIFIED } from '@pumpkincare/claims';

import { formatCurrency, parseAndFormatDate } from '../../../../shared/utils';
import { claimSubmissionInformationStyle } from './claim-submission-information-style';

export default function ClaimSubmissionInformation({ onClaimTypeChange, isSaving }) {
  const classes = claimSubmissionInformationStyle();
  const record = useRecordContext();
  const notify = useNotify();
  const [isEditingType, setIsEditingType] = useState(false);

  const canEditClaimType = record.status === UNVERIFIED;

  function handleClaimTypeSelect(event) {
    const nextIsEstimate = event.target.value === 'estimate';

    if (nextIsEstimate === record.is_estimate) {
      setIsEditingType(false);
      return;
    }

    onClaimTypeChange(record, nextIsEstimate)
      .then(() => {
        setIsEditingType(false);
      })
      .catch(error => {
        notify(
          `There was an error while updating the claim type: ${error.message}`,
          { type: 'error' }
        );
      });
  }

  return (
    <div className={classes.submissionDetail}>
      <div>
        <Typography className={classes.header}>Submission Details</Typography>
      </div>
      <div className={classes.titleWrapper}>
        <div>
          <Typography className={classes.title}>CLAIM ID</Typography>
          <Typography className={classes.detail}>{record.id}</Typography>
        </div>
        <div>
          <Typography className={classes.title}>TYPE</Typography>
          <Typography className={classes.detail}>{getClaimType(record)}</Typography>
        </div>
        <div>
          <Typography className={classes.title}>AMOUNT CLAIMED</Typography>
          <Typography className={classes.detail}>
            {formatCurrency(record.claimed_amount, { areCents: true })}
          </Typography>
        </div>
        <div>
          <Typography className={classes.title}>SUBMISSION DATE</Typography>
          <Typography className={classes.detail}>
            {parseAndFormatDate(record.date_submitted)}
          </Typography>
        </div>
        <div>
          <Typography className={classes.title}>INCIDENT DATE</Typography>
          <Typography className={classes.detail}>
            {parseAndFormatDate(record.loss_date)}
          </Typography>
        </div>
        <div>
          <Typography className={classes.title}>ESTIMATE</Typography>
          {isSaving ? (
            <CircularProgress size={16} />
          ) : isEditingType && canEditClaimType ? (
            <Select
              variant='outlined'
              size='small'
              autoFocus
              defaultValue={record.is_estimate ? 'estimate' : 'claim'}
              onChange={handleClaimTypeSelect}
              onClose={() => setIsEditingType(false)}
              data-testid='claim-type-select'
            >
              <MenuItem value='claim'>No</MenuItem>
              <MenuItem value='estimate'>Yes</MenuItem>
            </Select>
          ) : (
            <Button
              className={classes.claimTypeButton}
              onClick={() => setIsEditingType(true)}
              disabled={!canEditClaimType}
              data-testid='claim-type-display'
              title={
                canEditClaimType
                  ? 'Click to change'
                  : 'Can only be changed while the claim is Unverified'
              }
            >
              <Typography
                className={
                  canEditClaimType ? classes.claimTypeEditable : classes.detail
                }
              >
                {record.is_estimate === true ? 'Yes' : 'No'}
              </Typography>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
